import React from 'react';
import { RefreshCw, AlertTriangle, Smartphone, X, Stethoscope } from 'lucide-react';
import { DiagField } from './adminShared';

// Admin video diagnose modal — deep ffprobe + moov atom scan from object storage.
export const AdminVideoDiagnoseModal = ({
  closeDiagnose,
  diagnoseError,
  diagnoseLoading,
  diagnoseReport,
  diagnosingId,
  handleFaststartRemux,
  handleRecompress,
  videos,
}) => (
          <div
            className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={closeDiagnose}
            data-testid="video-diagnose-modal"
          >
            <div
              className="relative w-full max-w-2xl bg-zinc-900 rounded-2xl border border-zinc-800 overflow-hidden my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
                <div className="min-w-0 pr-4 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <p className="font-semibold text-white" data-testid="diagnose-title">
                    Video Diagnostic
                  </p>
                </div>
                <button
                  onClick={closeDiagnose}
                  className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
                  title="Close (Esc)"
                  data-testid="close-diagnose"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 space-y-4">
                {diagnoseLoading && (
                  <div className="flex items-center gap-2 text-sm text-zinc-400">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Downloading & probing video file from storage… (takes 5–30s)
                  </div>
                )}
                {diagnoseError && (
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-sm text-red-300" data-testid="diagnose-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{diagnoseError}</span>
                  </div>
                )}
                {diagnoseReport && (
                  <div className="space-y-4" data-testid="diagnose-report">
                    {/* Recommendation banner */}
                    <div
                      className={`rounded-lg p-3 border ${
                        diagnoseReport.recommendation === 'ok'
                          ? 'bg-green-500/10 border-green-500/30 text-green-300'
                          : diagnoseReport.recommendation === 'run_faststart_remux'
                          ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                          : diagnoseReport.recommendation === 'transient_storage_error'
                          ? 'bg-blue-500/10 border-blue-500/30 text-blue-200'
                          : 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                      }`}
                    >
                      <p className="font-semibold text-sm" data-testid="diagnose-recommendation">
                        {diagnoseReport.recommendation === 'ok' && 'Looks healthy — playback issue is probably client-side.'}
                        {diagnoseReport.recommendation === 'run_faststart_remux' && 'Run Faststart Remux to fix mobile playback.'}
                        {diagnoseReport.recommendation === 'run_recompress' && 'File is too large for smooth mobile playback — re-compress recommended.'}
                        {diagnoseReport.recommendation === 'reupload_required' && 'File is corrupted — ask the artist to re-upload.'}
                        {diagnoseReport.recommendation === 'missing_in_storage' && 'File missing from Object Storage — re-upload required.'}
                        {diagnoseReport.recommendation === 'transient_storage_error' && 'Transient storage/network issue — the file IS present, the download just got cut off. Retry the diagnostic; no re-upload needed.'}
                        {diagnoseReport.recommendation === 'delete_placeholder' && 'Empty placeholder — delete this row.'}
                      </p>
                      {(diagnoseReport.issues || []).length > 0 && (
                        <ul className="mt-2 list-disc list-inside text-xs space-y-0.5 opacity-90">
                          {diagnoseReport.issues.map((iss, i) => (
                            <li key={`issue-${i}-${iss.slice(0, 24)}`}>{iss}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Quick action: re-compress oversized files (≤70MB target) */}
                    {(diagnoseReport.recommendation === 'run_recompress' ||
                      (diagnoseReport.storage && diagnoseReport.storage.size_mb > 100)) && diagnoseReport.video_path && (
                      <button
                        onClick={async () => {
                          const v = videos.find((x) => x.id === diagnosingId);
                          if (!v) return;
                          closeDiagnose();
                          await handleRecompress(v);
                        }}
                        disabled={diagnoseReport.remux_status === 'running' || diagnoseReport.remux_status === 'recompressing'}
                        className="w-full px-4 py-2 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="diagnose-run-recompress"
                      >
                        <Smartphone className="w-4 h-4" />
                        {diagnoseReport.remux_status === 'recompressing'
                          ? 'Re-compress already running…'
                          : `Re-compress (${diagnoseReport.storage?.size_mb || '?'} MB → ≤70 MB)`}
                      </button>
                    )}

                    {/* Quick action: run faststart remux from the modal */}
                    {(diagnoseReport.recommendation === 'run_faststart_remux' ||
                      diagnoseReport.recommendation === 'ok') && diagnoseReport.video_path && (
                      <button
                        onClick={async () => {
                          const v = videos.find((x) => x.id === diagnosingId);
                          if (!v) return;
                          closeDiagnose();
                          await handleFaststartRemux(v);
                        }}
                        disabled={diagnoseReport.remux_status === 'running'}
                        className="w-full px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="diagnose-run-remux"
                      >
                        <Smartphone className="w-4 h-4" />
                        {diagnoseReport.remux_status === 'running' ? 'Remux already running…' : 'Run Faststart Remux now'}
                      </button>
                    )}

                    {/* Raw fields */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <DiagField label="Status" value={diagnoseReport.status} />
                      <DiagField label="DB Duration" value={diagnoseReport.duration_db ? `${diagnoseReport.duration_db}s` : '—'} />
                      <DiagField label="DB Size" value={diagnoseReport.file_size_mb_db ? `${diagnoseReport.file_size_mb_db} MB` : '—'} />
                      <DiagField label="Remux Status" value={diagnoseReport.remux_status || '—'} />
                      <DiagField
                        label="Compress Status"
                        value={diagnoseReport.compress_status || '—'}
                        warn={diagnoseReport.compress_status === 'failed' || diagnoseReport.compress_status === 'skipped_no_savings'}
                      />
                      {diagnoseReport.compress_error && (
                        <div className="col-span-2 px-2 py-1 rounded bg-amber-500/10 text-amber-200 text-[11px]">
                          Compress error: {diagnoseReport.compress_error}
                        </div>
                      )}
                      <DiagField
                        label="Storage"
                        value={
                          diagnoseReport.storage
                            ? `${diagnoseReport.storage.size_mb} MB · ${diagnoseReport.storage.content_type}`
                            : 'Not found'
                        }
                      />
                      <DiagField
                        label="Moov atom"
                        value={
                          diagnoseReport.moov_atom
                            ? `${diagnoseReport.moov_atom.present ? 'present' : 'MISSING'} · ${diagnoseReport.moov_atom.position || '—'}`
                            : '—'
                        }
                        warn={diagnoseReport.moov_atom && (!diagnoseReport.moov_atom.present || diagnoseReport.moov_atom.position === 'end')}
                      />
                      {diagnoseReport.ffprobe && !diagnoseReport.ffprobe.error && (
                        <>
                          <DiagField label="Container" value={diagnoseReport.ffprobe.container || '—'} />
                          <DiagField label="Duration (probe)" value={diagnoseReport.ffprobe.duration_sec ? `${diagnoseReport.ffprobe.duration_sec.toFixed(1)}s` : '—'} />
                          <DiagField label="Video codec" value={`${diagnoseReport.ffprobe.video_codec || '—'} ${diagnoseReport.ffprobe.video_width || ''}×${diagnoseReport.ffprobe.video_height || ''}`} />
                          <DiagField label="Audio codec" value={diagnoseReport.ffprobe.audio_codec || '—'} />
                        </>
                      )}
                      {diagnoseReport.ffprobe?.error && (
                        <div className="col-span-2 px-2 py-1 rounded bg-red-500/10 text-red-300 text-[11px]" data-testid="diagnose-ffprobe-error">
                          ffprobe: {diagnoseReport.ffprobe.error}
                        </div>
                      )}
                    </div>

                    <details className="text-xs text-zinc-500">
                      <summary className="cursor-pointer hover:text-zinc-300">Raw JSON</summary>
                      <pre className="mt-2 p-2 bg-black/40 rounded border border-zinc-800 overflow-x-auto text-[10px] text-zinc-400">
  {JSON.stringify(diagnoseReport, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </div>
          </div>
);
